import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import { Text } from '@chakra-ui/react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { useSaveConflictStore } from '../../state/useSaveConflictStore';

/**
 * Save-conflict dialogs (Faz 9.1) — mounted once by ProjectDialogsHost next
 * to the other blocking file dialogs. ExternalChange asks before overwriting
 * a file another program touched; FileInUse asks after a sharing violation
 * survived the desktop retry window.
 */
export function SaveConflictDialogs() {
    const { t } = useTranslation();
    const externalFileName = useSaveConflictStore((s) => s.externalFileName);
    const inUseFileName = useSaveConflictStore((s) => s.inUseFileName);
    const closeExternalChange = useSaveConflictStore((s) => s.closeExternalChange);
    const closeFileInUse = useSaveConflictStore((s) => s.closeFileInUse);
    const cancelExternalRef = useRef<HTMLButtonElement>(null);
    const cancelInUseRef = useRef<HTMLButtonElement>(null);

    return (
        <>
            <AppDialog
                isOpen={externalFileName !== null}
                onClose={() => closeExternalChange(false)}
                title={t('dialogs.ui.fileChangedOnDisk')}
                variant="alert"
                initialFocusRef={cancelExternalRef}
                footer={
                    <>
                        <DialogButton ref={cancelExternalRef} variant="secondary" onClick={() => closeExternalChange(false)}>
                            {t('dialogs.ui.cancel')}
                        </DialogButton>
                        <DialogButton variant="primary" onClick={() => closeExternalChange(true)}>
                            {t('dialogs.ui.saveAnyway')}
                        </DialogButton>
                    </>
                }
            >
                <Text mb={2}>
                    {t('dialogs.ui.fileChangedOnDiskBody')}{' '}
                    <Text as="span" fontWeight="semibold">{externalFileName}</Text>{' '}
                    {t('dialogs.ui.fileChangedOnDiskTail')}
                </Text>
                <Text fontSize="sm" color="var(--color-text-muted)">
                    {t('dialogs.ui.fileChangedOnDiskHint')}
                </Text>
            </AppDialog>

            <AppDialog
                isOpen={inUseFileName !== null}
                onClose={() => closeFileInUse(false)}
                title={t('dialogs.ui.fileInUse')}
                variant="alert"
                initialFocusRef={cancelInUseRef}
                footer={
                    <>
                        <DialogButton ref={cancelInUseRef} variant="secondary" onClick={() => closeFileInUse(false)}>
                            {t('dialogs.ui.cancel')}
                        </DialogButton>
                        <DialogButton variant="primary" onClick={() => closeFileInUse(true)}>
                            {t('dialogs.ui.retrySave')}
                        </DialogButton>
                    </>
                }
            >
                <Text mb={2}>
                    <Text as="span" fontWeight="semibold">{inUseFileName}</Text>{' '}
                    {t('dialogs.ui.fileInUseBody')}
                </Text>
                <Text fontSize="sm" color="var(--color-text-muted)">
                    {t('dialogs.ui.fileInUseHint')}
                </Text>
            </AppDialog>
        </>
    );
}
